/// <reference lib="dom" />

import 'htmx.org';
import Sortable from 'sortablejs';

const MAX_PARALLEL_UPLOADS = 2;
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

type UploadTask = {
  file: File;
  clientUploadId: string;
  previewUrl: string;
  element: HTMLElement;
};

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
}

function uploadOne(form: HTMLFormElement, task: UploadTask): Promise<void> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    const body = new FormData();
    body.set('client_upload_id', task.clientUploadId);
    body.set('image', task.file);
    xhr.open('POST', form.action);
    xhr.setRequestHeader('X-CSRF-Token', csrfToken());
    xhr.setRequestHeader('X-Client-Upload-Id', task.clientUploadId);
    xhr.setRequestHeader('HX-Request', 'true');
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        task.element.querySelector<HTMLProgressElement>('[data-upload-progress-bar]')!.value =
          progress;
        task.element.querySelector<HTMLElement>('[data-upload-progress]')!.textContent =
          `${progress}%`;
      }
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        URL.revokeObjectURL(task.previewUrl);
        task.element.outerHTML = xhr.responseText;
      } else {
        task.element.dataset.uploadState = 'failed';
        task.element.querySelector<HTMLElement>('[data-upload-status]')!.textContent =
          'A feltöltés nem sikerült. Próbáld újra.';
        task.element.querySelector<HTMLButtonElement>('[data-upload-retry]')!.hidden = false;
      }
      resolve();
    });
    xhr.addEventListener('error', () => {
      task.element.dataset.uploadState = 'failed';
      task.element.querySelector<HTMLElement>('[data-upload-status]')!.textContent =
        'Hálózati hiba történt.';
      task.element.querySelector<HTMLButtonElement>('[data-upload-retry]')!.hidden = false;
      resolve();
    });
    xhr.send(body);
  });
}

async function runUploadQueue(form: HTMLFormElement, tasks: UploadTask[]): Promise<void> {
  const queue = [...tasks];
  const worker = async (): Promise<void> => {
    while (queue.length > 0) {
      const task = queue.shift();
      if (task) await uploadOne(form, task);
    }
  };
  await Promise.all(Array.from({ length: Math.min(MAX_PARALLEL_UPLOADS, tasks.length) }, worker));
}

function createPreview(file: File): UploadTask {
  const clientUploadId = crypto.randomUUID();
  const previewUrl = URL.createObjectURL(file);
  const element = document.createElement('article');
  element.className = 'image-card image-card--uploading';
  element.setAttribute('aria-live', 'polite');
  element.innerHTML =
    '<img data-upload-preview alt="Helyi képelőnézet">' +
    '<div><strong data-upload-name></strong><span data-upload-size></span></div>' +
    '<p data-upload-status>Feltöltésre vár</p><progress data-upload-progress-bar max="100"></progress>' +
    '<span data-upload-progress>0%</span><button type="button" data-upload-retry hidden>Újrapróbálás</button>' +
    '<button type="button" data-upload-cancel>Törlés</button>';
  element.querySelector<HTMLImageElement>('[data-upload-preview]')!.src = previewUrl;
  element.querySelector<HTMLElement>('[data-upload-name]')!.textContent = file.name;
  element.querySelector<HTMLElement>('[data-upload-size]')!.textContent =
    ` – ${(file.size / 1024 / 1024).toFixed(1)} MiB`;
  return { file, clientUploadId, previewUrl, element };
}

function initializeUploads(root: ParentNode = document): void {
  for (const input of root.querySelectorAll<HTMLInputElement>('[data-image-input]')) {
    if (input.dataset.initialized) continue;
    input.dataset.initialized = 'true';
    input.addEventListener('change', () => {
      const form = input.closest<HTMLFormElement>('form[data-upload-form]');
      const list = document.querySelector<HTMLElement>('[data-image-list]');
      if (!form || !list || !input.files) return;
      const availableSlots = Math.max(
        0,
        20 - list.querySelectorAll('[data-image-public-id]').length,
      );
      const files = [...input.files].slice(0, availableSlots).filter((file) => {
        if (file.size > MAX_UPLOAD_BYTES || !ACCEPTED_IMAGE_TYPES.has(file.type)) {
          list.insertAdjacentHTML(
            'afterbegin',
            '<p class="error-summary" aria-live="assertive">Csak legfeljebb 15 MiB-os JPEG, PNG vagy WebP kép választható.</p>',
          );
          return false;
        }
        return true;
      });
      const tasks = files.map(createPreview);
      for (const task of tasks) {
        const retryButton = task.element.querySelector<HTMLButtonElement>('[data-upload-retry]')!;
        retryButton.addEventListener('click', () => {
          retryButton.hidden = true;
          void uploadOne(form, task);
        });
        task.element
          .querySelector<HTMLButtonElement>('[data-upload-cancel]')!
          .addEventListener('click', () => {
            URL.revokeObjectURL(task.previewUrl);
            task.element.remove();
          });
        list.append(task.element);
      }
      void runUploadQueue(form, tasks);
      input.value = '';
    });
  }
}

function initializeSortable(root: ParentNode = document): void {
  for (const list of root.querySelectorAll<HTMLElement>('[data-sortable-images]')) {
    if (list.dataset.sortableInitialized) continue;
    list.dataset.sortableInitialized = 'true';
    Sortable.create(list, {
      animation: 150,
      handle: '[data-drag-handle]',
      onEnd: () => {
        const url = list.dataset.reorderUrl;
        if (!url) return;
        const ids = [...list.querySelectorAll<HTMLElement>('[data-image-public-id]')].flatMap(
          (item) => (item.dataset.imagePublicId ? [item.dataset.imagePublicId] : []),
        );
        void fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken(),
            'HX-Request': 'true',
          },
          body: JSON.stringify({ imagePublicIds: ids }),
        }).then((response) => {
          if (!response.ok) window.location.reload();
        });
      },
    });
  }
}

function initialize(root: ParentNode = document): void {
  initializeUploads(root);
  initializeSortable(root);
}

document.addEventListener('DOMContentLoaded', () => initialize());
document.addEventListener('htmx:afterSwap', (event) => {
  initialize((event.target as ParentNode | null) ?? document);
});
